// Copyright 2024, Durable

import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { DurableLogo } from "../components/shared/DurableLogo";
import { NotificationButton } from "../components/shared/NotificationDropdown";
import { UserPortal } from "./UserPortal";
import { Button, IconButton } from "@mui/material";
import { Close as CloseIcon, Menu as MenuIcon } from "@mui/icons-material";
import { useAuthContext } from "../components/shared/Auth";
import { UserPermission } from "@durable/durable-client";
import userPages, { route } from "../routes/userPages";
import supportPages from "../routes/supportPages";

/**
 * Provides navigation for all pages across Durable. If large, provides a
 * sidebar with additional options. If small, provides only a basic navigation
 * bar. Also allows for override into either mode.
 */
function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);

  /**
   * Open the mobile menu
   */
  function openMenu(clickEvent: React.MouseEvent) {
    if (menuOpen) {
      setMenuOpen(false);
      clickEvent.stopPropagation();
      clickEvent.preventDefault();
    }
  }

  /* eslint-disable jsx-a11y/no-static-element-interactions */
  /* eslint-disable jsx-a11y/click-events-have-key-events */
  return (
    <>
      <TopNavBar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <div className="flex overflow-hidden">
        <SideNavBar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
        <div
          id="navigation-size-reservation"
          className={"transition-all w-full p-0 pt-14 "}
          onClick={openMenu}
        >
          <Outlet />
        </div>
      </div>
    </>
  );

  /* eslint-enable jsx-a11y/no-static-element-interactions */
  /* eslint-enable jsx-a11y/click-events-have-key-events */
}

/**
 * The top navigation bar. Used on very small screens to save horizontal space.
 * On wider screens, used to provide user tools and the logo.
 */
function TopNavBar({
  menuOpen,
  setMenuOpen
}: {
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
}) {
  return (
    <nav
      id="navbar"
      className={`w-screen h-14 fixed top-0 left-0 transition-all z-30
      flex items-center justify-between select-none shadow-lg lg:shadow-none
    bg-coolGrey-1000 dark:bg-coolGrey-300`}
    >
      {/* The left side of the navbar containing the logo and sidebar button */}
      <div id="logo-box" className="flex text-3xl px-4">
        <div className="ml-2 flex items-center">
          <IconButton
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-coolGrey-500 dark:text-coolGrey-600"
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </div>
        <DurableLogo variant="dynamic" size={165} />
        <div className="my-2 -ml-2.5 text-coolGrey-500 dark:text-coolGrey-600">
          .ai
        </div>
      </div>

      {/* The right side of the navbar containing controls */}
      <div
        id="control-box"
        className={`flex gap-2 px-4 p-1 lg:bg-coolGrey-900/80 
        dark:lg:bg-coolGrey-200/80 rounded-full`}
      >
        <NotificationButton />
        <UserPortal />
      </div>
    </nav>
  );
}

/**
 * The side navigation bar. Used on wide screens to provide additional
 * navigation options.
 */
function SideNavBar({
  menuOpen,
  setMenuOpen
}: {
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
}) {
  return (
    <>
      <nav
        id="sidebar"
        className={`h-screen overflow-hidden overflow-y-auto select-none
        transition-all fixed top-0 left-0 z-20 p-0 pt-16
      ${
        menuOpen
          ? "w-64 shadow-xl px-4 pt-16 bg-coolGrey-1000 dark:bg-coolGrey-100"
          : "w-0 pt-16"
      }`}
      >
        <UserPageLinks menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
        <SupportPageLinks menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      </nav>
    </>
  );
}

/**
 * Links to each of the user-accessible pages.
 */
function UserPageLinks({
  menuOpen,
  setMenuOpen
}: {
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 mt-2">
      {userPages.map((userPage) => (
        <PageLink
          key={userPage.id}
          page={userPage}
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
        />
      ))}
    </div>
  );
}

/**
 * Links to each of the support-accessible pages, if the user has permission to
 * access debug tools.
 */
function SupportPageLinks({
  menuOpen,
  setMenuOpen
}: {
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
}) {
  const credentials = useAuthContext();
  return (
    <>
      {credentials.user &&
        credentials.user.permissions.includes(UserPermission.DEBUG_TOOLS) && (
          <>
            <div
              className={`mt-8 mb-3 ml-3 text-sm text-coolGrey-400
            dark:text-coolGrey-700 w-56`}
            >
              Support and Development
            </div>
            <div className="grid grid-cols-1 gap-2">
              {supportPages.map((supportPage) => (
                <PageLink
                  key={supportPage.id}
                  page={supportPage}
                  menuOpen={menuOpen}
                  setMenuOpen={setMenuOpen}
                />
              ))}
            </div>
          </>
        )}
    </>
  );
}

/**
 * A button linking to an individual route on durable. Highlights if that
 * route is already active and prevents reselection.
 */
function PageLink({
  page,
  menuOpen,
  setMenuOpen
}: {
  page: route;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  tabIndex?: number;
}) {
  const [isCurrentPage, setIsCurrentPage] = useState<boolean>(false);
  const currentLocation = useLocation();
  useEffect(() => {
    if (!page.routeOnly) {
      const currentRoute = currentLocation.pathname;
      if (currentRoute == page.route) {
        setIsCurrentPage(true);
      } else {
        setIsCurrentPage(false);
      }
    }
  }, [currentLocation, page]);

  if (page.routeOnly) {
    return null;
  }

  // Disable tabbing to the link, so that tabbing focuses the button
  return (
    <Link
      to={page.route}
      className="no-underline cursor-auto"
      tabIndex={-1}
      onClick={() => {
        setMenuOpen(false);
      }}
    >
      <Button
        className={
          `text-coolGrey-400 dark:text-coolGrey-700
           w-56 px-4 flex justify-start ` +
          `disabled:bg-coolGrey-800 disabled:text-coolGrey-200
           disabled:dark:bg-coolGrey-300 disabled:dark:text-coolGrey-1000`
        }
        size="large"
        disabled={isCurrentPage}
        startIcon={page.icon()}
        color="inherit"
        tabIndex={menuOpen ? 0 : -1}
      >
        {page.name}
      </Button>
    </Link>
  );
}

export { Navigation };
